import type {
  ElementType,
  HeaderScope,
  PhotoRequirement,
  TemplateSettings,
  TemplateTypeValue,
} from './types';
import {DEFAULT_PAGE_NUMBER_CONFIG} from './page-numbers';

export const HEADER_GROUP_ID = '__page_header__';

/**
 * Resolve a header sentinel's display scope, falling back to the legacy
 * `repeat_on_every_page` boolean for templates saved before `header_scope` existed:
 *   repeat_on_every_page === false → first page only ({ from: 1, to: 1 })
 *   otherwise                      → every page ({ from: 1 })
 */
export function getHeaderScope(
  sentinel: Pick<PhotoRequirement, 'header_scope' | 'repeat_on_every_page'>,
): HeaderScope {
  if (sentinel.header_scope) return sentinel.header_scope;
  if (sentinel.repeat_on_every_page === false) return {from: 1, to: 1};
  return {from: 1};
}

export const ELEMENT_TYPE_LABELS: Record<ElementType, string> = {
  group: 'Group',
  header: 'Text Label',
  photo: 'Image input',
  logo: 'Photo',
  text: 'Text input',
  table: 'Table',
  checkbox: 'Checkbox',
  dropdown: 'Dropdown',
  signature: 'Signature',
  page_break: 'Page Break',
  page_number: 'Page Number',
  qr_code: 'QR Code',
  divider: 'Divider',
};

export const ELEMENT_TYPE_GROUPS = [
  {
    // Static display content
    group: 'Content',
    types: ['header', 'qr_code'] as ElementType[],
  },
  {
    // Images
    group: 'Media',
    types: ['photo', 'logo'] as ElementType[],
  },
  {
    // Worker-filled fields
    group: 'Inputs',
    types: ['text', 'checkbox', 'dropdown', 'signature', 'table'] as ElementType[],
  },
  {
    // Page structure
    group: 'Layout',
    types: ['divider', 'page_break', 'page_number'] as ElementType[],
  },
];

export function blankRequirement(type: ElementType = 'text', order = 0): PhotoRequirement {
  const base = {
    id: crypto.randomUUID(),
    label: ELEMENT_TYPE_LABELS[type],
    description: '',
    required: false,
    order,
    section: '',
    style: {},
  };
  // Per-type config defaults. A `switch` (rather than an indexed map) lets each
  // branch's `type` literal narrow the return to the matching union variant, so
  // the strictly-typed config defaults are checked without a cast.
  switch (type) {
    case 'header':
      return {...base, type, style: {font_size: 16, font_weight: 'bold'}, config: {}};
    case 'photo':
      return {
        ...base,
        type,
        config: {
          // A new image input is square. The type's own default was already
          // 200x200, so this changes nothing about how one arrives -- it records
          // the intent, so widening it later keeps it square instead of
          // silently becoming a rectangle.
          square: true,
          show_logo: true,
          show_compass: true,
          show_datetime: true,
          show_latlng: true,
          show_map: true,
          allow_pan: true,
        },
      };
    case 'signature':
      // No `label`. It printed a grey "Signature" caption above a pad that
      // already says so inside it -- and the element's own `label` (from
      // `ELEMENT_TYPE_LABELS`) is the third copy, in the layers list. A caption
      // earns its place when it distinguishes two pads on one page ("Customer",
      // "Technician"), which is a thing an author decides, not a default. The
      // Label field in `ItemEditor` placeholders "Signature" so an empty one
      // still reads as deliberate, and typing into it brings the caption back.
      return {...base, type, config: {show_signer_name: true, show_signed_at: true}};
    case 'checkbox':
      return {...base, type, config: {options: ['Option 1'], multi_select: false}};
    case 'dropdown':
      return {...base, type, config: {options: ['Option 1']}};
    case 'table':
      return {...base, type, config: {rows: [], columns: []}};
    case 'qr_code':
      return {...base, type, config: {size: 96, ecc_level: 'M'}};
    // A page number is a stamp, not a line of content: it occupies a fixed band —
    // the page's own bottom (or top) margin — and repeats on every page from there.
    // It carries NO coordinates: `placement` + `align` are the whole of its
    // position, so there is nothing here for a drag to write.
    //
    // `position_mode: 'absolute'` is NOT a claim that it is placed by coordinates —
    // the layout ignores it entirely (`pageNumberStamps` in usePreviewLayout is its
    // own bucket). It says only "not in the flow", which is what keeps the toolbar's
    // flow-width control off it. Everything that asks whether an element is a stamp
    // asks `isPageNumber`, never this field.
    case 'page_number':
      return {
        ...base,
        type,
        label: '',
        position_mode: 'absolute',
        style: {font_size: DEFAULT_PAGE_NUMBER_FONT_SIZE},
        config: {...DEFAULT_PAGE_NUMBER_CONFIG},
      };
    case 'text':
    case 'logo':
    case 'divider':
    case 'group':
    case 'page_break':
      return {...base, type, config: {}};
  }
}

/**
 * Font size in px an element's text renders at when its style sets none. Shared by
 * the renderers (as the `buildTextStyle` default) and by the toolbar's Size field, so
 * the number in the box is the number on the page.
 */
export const DEFAULT_TEXT_FONT_SIZE = 14;

/** A page-number stamp is print furniture, not content — smaller than body text by
 *  default. Both the stamp a new element starts with and the renderer's fallback
 *  read this, so the two cannot drift. */
export const DEFAULT_PAGE_NUMBER_FONT_SIZE = 10;

/** As {@link DEFAULT_TEXT_FONT_SIZE}, for the smaller checkbox / dropdown option rows. */
export const DEFAULT_OPTION_FONT_SIZE = 12;

/**
 * As {@link DEFAULT_TEXT_FONT_SIZE}, for a text input's own caption.
 *
 * 10px, which is what the signature's label has printed at all along — a
 * caption is meant to be read after the answer it names, not before it.
 */
export const LABEL_DEFAULT_FONT_SIZE = 10;

/** Line height for those rows, as a ratio so it tracks whatever size the author sets.
 *  4/3 is what Tailwind's `text-xs` gave them before the size moved into the style. */
export const DEFAULT_OPTION_LINE_HEIGHT = 4 / 3;

export const DEFAULT_SETTINGS: TemplateSettings = {
  page: {
    size: 'A4',
    orientation: 'portrait',
    margin_top: 40,
    margin_bottom: 40,
    margin_left: 40,
    margin_right: 40,
  },
  grid: {
    columns: 12,
    row_height: 20,
    gap: 4,
  },
  default_styles: {
    font_family: 'Inter, sans-serif',
    font_size: 12,
    color: '#000000',
    background_color: '#ffffff',
  },
};

export function makeEmptyTemplate(templateType: TemplateTypeValue = 'photo_report') {
  return {
    name: '',
    description: '',
    project_ids: [] as string[],
    template_type: templateType,
    service_type_ids: [] as string[],
    requirements: [] as PhotoRequirement[],
    settings: DEFAULT_SETTINGS,
  };
}
